import { NewsPost } from '../types'

// Import all markdown files from the content/news directory
const markdownFiles = import.meta.glob('/content/news/*.md', { 
  query: '?raw',
  import: 'default',
  eager: true 
})

// Simple frontmatter parser (lightweight alternative to gray-matter)
function parseFrontmatter(content: string): { data: Record<string, string | string[]>, content: string } {
  const frontmatterRegex = /^---\s*\n([\s\S]*?)\n---\s*\n?/m
  const match = content.match(frontmatterRegex)
  
  if (!match) {
    return { data: {}, content }
  }
  
  const frontmatter = match[1]
  const body = content.slice(match[0].length)
  
  const data: Record<string, string | string[]> = {}
  const lines = frontmatter.split('\n')
  
  let currentKey = ''
  let isArray = false
  let arrayValues: string[] = []
  
  for (const line of lines) {
    const trimmed = line.trim()
    
    // Check if this is a new key-value pair
    const keyValueMatch = trimmed.match(/^(\w+):\s*(.*)$/)
    
    if (keyValueMatch) {
      // Save previous array if we were collecting one
      if (isArray && currentKey) {
        data[currentKey] = arrayValues
        arrayValues = []
        isArray = false
      }
      
      currentKey = keyValueMatch[1]
      const value = keyValueMatch[2].trim()
      
      // Check if this starts an array
      if (value === '[') {
        isArray = true
      } else if (value.startsWith('[') && value.endsWith(']')) {
        // Single-line array
        data[currentKey] = value
          .slice(1, -1)
          .split(',')
          .map(v => v.trim().replace(/^['"]|['"]$/g, ''))
          .filter(v => v)
      } else {
        // Regular string value
        data[currentKey] = value.replace(/^['"]|['"]$/g, '')
      }
    } else if (isArray && trimmed.startsWith('- ')) {
      // Array item
      arrayValues.push(trimmed.slice(2).replace(/^['"]|['"]$/g, ''))
    } else if (isArray && trimmed === ']') {
      // End of array
      data[currentKey] = arrayValues
      arrayValues = []
      isArray = false
    }
  }
  
  // Handle case where array doesn't have closing bracket
  if (isArray && currentKey && arrayValues.length > 0) {
    data[currentKey] = arrayValues
  }
  
  return { data, content: body }
}

export async function loadNewsPosts(): Promise<NewsPost[]> {
  const posts: NewsPost[] = []
  
  for (const [path, content] of Object.entries(markdownFiles)) {
    // Extract slug from path: /content/news/post-slug.md
    const slug = path.replace('/content/news/', '').replace('.md', '')
    
    const { data, content: markdownContent } = parseFrontmatter(content as string)
    
    posts.push({
      slug,
      title: data.title as string,
      excerpt: data.excerpt as string,
      date: data.date as string,
      tags: (data.tags as string[]) || [],
      content: markdownContent
    })
  }
  
  // Sort by date descending (newest first)
  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}

// Synchronous version for use with eager imports
export function getNewsPostsSync(): NewsPost[] {
  const posts: NewsPost[] = []
  
  for (const [path, content] of Object.entries(markdownFiles)) {
    // Extract slug from path: /content/news/post-slug.md
    const slug = path.replace('/content/news/', '').replace('.md', '')
    
    const { data, content: markdownContent } = parseFrontmatter(content as string)
    
    posts.push({
      slug,
      title: data.title as string,
      excerpt: data.excerpt as string,
      date: data.date as string,
      tags: (data.tags as string[]) || [],
      content: markdownContent
    })
  }
  
  // Sort by date descending (newest first)
  return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
}
